/** Ciclo editorial; independiente del estado published/draft y de la fecha del artículo. */
export type NewsState = 'active' | 'resolved' | 'superseded' | 'archived';
export type NewsSource = { label: string; url: string; visibility: 'public' } |
  { reference: string; visibility: 'private' };
export type BlogNews = {
  state: NewsState;
  effectiveFrom: string;
  effectiveTo?: string;
  verifiedAt: string;
  nextReviewAt: string;
  reviewOwner: string;
  sources: NewsSource[];
  relatedPagePaths: string[];
  supersededBy?: string;
  resolvedAt?: string;
};
export type PublicBlogNews = Omit<BlogNews, 'reviewOwner' | 'sources'> & {
  sources: { label: string; url: string }[];
};
export type NewsPresentation = NewsState | 'scheduled' | 'expired' | 'review_due' | 'unverified';
export type PublicNewsItem = {
  path: string;
  title: string;
  description: string;
  publishedAt: string;
  updatedAt: string;
  subtype: 'noticia' | 'aviso_operativo';
  news: PublicBlogNews;
};
export type PublicNewsFeed = { serverNow: number; items: PublicNewsItem[] };

export function newsTimestamp(value: string): number {
  const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (!parts) {
    throw new Error('Actualidad: la fecha requiere hora y zona ISO');
  }
  const [, year, month, day, hour, minute, second] = parts.map(Number);
  // Date.parse normaliza días inexistentes (p. ej. 30 de febrero).
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth || hour > 23 || minute > 59 || second > 59) {
    throw new Error('Actualidad: fecha de calendario inválida');
  }
  const time = Date.parse(value);
  if (!Number.isFinite(time)) throw new Error('Actualidad: fecha inválida');
  return time;
}
const localPath = (path: string) => /^\/(?:blog|tours)\/[a-z0-9-]+$/.test(path);
export function validateBlogNews(news: BlogNews): void {
  if (!['active','resolved','superseded','archived'].includes(news.state)) throw new Error('Actualidad: estado desconocido');
  const from = newsTimestamp(news.effectiveFrom);
  const verified = newsTimestamp(news.verifiedAt);
  if (news.effectiveTo && newsTimestamp(news.effectiveTo) <= from) throw new Error('Actualidad: fin anterior al inicio');
  if (newsTimestamp(news.nextReviewAt) <= verified) throw new Error('Actualidad: revisión debe seguir a la verificación');
  if (!news.reviewOwner.trim() || !news.sources.length) throw new Error('Actualidad: faltan responsable o fuentes');
  for (const source of news.sources) {
    if (source.visibility === 'private') {
      if (!source.reference.trim()) throw new Error('Actualidad: referencia privada vacía');
    } else if (source.visibility === 'public') {
      const url = new URL(source.url);
      if (!source.label.trim() || url.protocol !== 'https:' || url.username || url.password) throw new Error('Actualidad: fuente pública inválida');
    } else throw new Error('Actualidad: visibilidad de fuente desconocida');
  }
  if (news.relatedPagePaths.some(path => !localPath(path))) throw new Error('Actualidad: relación debe ser una página local');
  if (new Set(news.relatedPagePaths).size !== news.relatedPagePaths.length) throw new Error('Actualidad: relación duplicada');
  if (news.supersededBy && !/^\/blog\/[a-z0-9-]+$/.test(news.supersededBy)) throw new Error('Actualidad: reemplazo inválido');
  if (news.state === 'superseded' && (!news.supersededBy || !/^\/blog\/[a-z0-9-]+$/.test(news.supersededBy))) throw new Error('Actualidad: falta pieza de reemplazo');
  if (news.resolvedAt && newsTimestamp(news.resolvedAt) > verified) throw new Error('Actualidad: resolución posterior a la verificación');
  if (news.state === 'resolved' && !news.resolvedAt) throw new Error('Actualidad: resolución necesita fecha verificada');
  if (news.state === 'active' && (news.resolvedAt || news.supersededBy)) throw new Error('Actualidad: aviso activo con cierre contradictorio');
}

/** Lista blanca: ni el responsable ni las referencias operativas llegan al cliente. */
export function publicBlogNews(news: BlogNews): PublicBlogNews {
  validateBlogNews(news);
  return {
    state:news.state,effectiveFrom:news.effectiveFrom,effectiveTo:news.effectiveTo,
    verifiedAt:news.verifiedAt,nextReviewAt:news.nextReviewAt,
    relatedPagePaths:news.relatedPagePaths,
    supersededBy:news.supersededBy,resolvedAt:news.resolvedAt,
    sources:news.sources.flatMap(source => source.visibility === 'public' ? [{label:source.label,url:source.url}] : []),
  };
}

/** El reloj cambia la presentación, nunca el hecho ni los datos persistidos. */
export function newsPresentation(news: PublicBlogNews, now: number | null): NewsPresentation {
  if (news.state !== 'active') return news.state;
  if (now === null || !Number.isFinite(now) || now < Date.parse(news.verifiedAt)) return 'unverified';
  if (news.effectiveTo && now >= Date.parse(news.effectiveTo)) return 'expired';
  if (now >= Date.parse(news.nextReviewAt)) return 'review_due';
  if (now < Date.parse(news.effectiveFrom)) return 'scheduled';
  return 'active';
}

export const NEWS_PRESENTATION_LABELS: Record<NewsPresentation,string> = {
  active:'Aviso vigente verificado', scheduled:'Cambio anunciado para una fecha futura',
  expired:'Vigencia declarada finalizada; no implica resolución verificada',
  review_due:'Pendiente de nueva verificación', unverified:'Vigencia pendiente de comprobar',
  resolved:'Resolución verificada', superseded:'Publicación reemplazada', archived:'Publicación archivada',
};

export function formatNewsDate(value: string | number): string {
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Bogota',
  }).format(new Date(value));
}
